/**
 * SPDX-FileCopyrightText: 2025 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import { FileAction, Permission, registerFileAction } from '@nextcloud/files';
import { generateUrl } from '@nextcloud/router';
import { getCapabilities } from '@nextcloud/capabilities';
import { t } from '@nextcloud/l10n';
import AutoRenewSvg from '@mdi/svg/svg/autorenew.svg?raw';
import { convertFile, convertFiles } from './convertUtils';
import { isPublicShare } from '@nextcloud/sharing/public';
import { useFilesStore } from '../store/files';
export const ACTION_CONVERT = 'convert';
export const registerConvertActions = () => {
    // Generate sub actions
    const convertProviders = getCapabilities()?.files?.file_conversions ?? [];
    const actions = convertProviders.map(({ to, from, displayName }) => {
        return new FileAction({
            id: `convert-${from}-${to}`,
            displayName: () => t('files', 'Save as {displayName}', { displayName }),
            iconSvgInline: () => generateIconSvg(to),
            enabled: (nodes, view) => {
                if (isPublicShare()) {
                    // public shares without create permission cant convert files (no way to save the results)
                    const store = useFilesStore();
                    const root = store.getRoot(view.id);
                    if (root && !(root.permissions & Permission.CREATE)) {
                        return false;
                    }
                }
                // Check that all nodes have the same mime type
                return nodes.every(node => from === node.mime);
            },
            async exec(node) {
                // If we're here, we know that the node has a fileid
                convertFile(node.fileid, to);
                // Silently terminate, we'll handle the UI in the background
                return null;
            },
            async execBatch(nodes) {
                const fileIds = nodes.map(node => node.fileid).filter(Boolean);
                convertFiles(fileIds, to);
                // Silently terminate, we'll handle the UI in the background
                return Array(nodes.length).fill(null);
            },
            parent: ACTION_CONVERT,
        });
    });
    // Register main action
    registerFileAction(new FileAction({
        id: ACTION_CONVERT,
        displayName: () => t('files', 'Save as …'),
        iconSvgInline: () => AutoRenewSvg,
        enabled: (nodes, view) => {
            return actions.some(action => action.enabled(nodes, view));
        },
        async exec() {
            return null;
        },
        order: 25,
    }));
    // Register sub actions
    actions.forEach(registerFileAction);
};
export const generateIconSvg = (mime) => {
    // Generate icon based on mime type
    const url = generateUrl('/core/mimeicon?mime=' + encodeURIComponent(mime));
    return `<svg width="32" height="32" viewBox="0 0 32 32"
		xmlns="http://www.w3.org/2000/svg">
		<image href="${url}" height="32" width="32" />
	</svg>`;
};
